import { useEffect, useState } from "react";
import { Calendar as BigCalendar, momentLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import moment from "moment";
import AddAppointment from "./AddAppointment";
import "./Calendar.css";
import DefineSlotOrder from "./DefineSlotOrder";
import { useNavigate } from "react-router-dom";
import ProspectiveTenants from "./ProspectiveTenants";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import { fetchApartmentsListingsDataWithAppointments } from "./DataServices/ApartmentsListingsDataServices";
import {
  fetchConfirmedAppointments,
  fetchAppointmentsPerListingData,
} from "./DataServices/AppointmentsDataServices";
import Notification from "./Notification";
import dayjs from "dayjs";
import ComposeEmail from "./ComposeEmail";
import { CalendarEvent, IAvailableAppointment } from "./Models";

const localizer = momentLocalizer(moment);

interface IProspectiveTenant {
  ID: string;
  email: string;
  firstName: string;
  lastName: string;
  notes: string;
}

interface IListing {
  id: string;
  idApartment: string;
  title: string;
  description: string;
  rent: string;
  moveInDate: string;
  showings: string;
  areAvailableAppointmentsSet: boolean;
  isSelected: boolean;
  isVacant: boolean;
}

interface ICalendarProps {
  relatedListing: IListing[];
  showAvailableAppointments: boolean;
  setShowCalendar?: React.Dispatch<React.SetStateAction<boolean>>;
  disableMainAppointmentFetch?: boolean;
}

//availableAppointments, relatedListings
function Calendar({
  setShowCalendar,
  relatedListing,
  showAvailableAppointments,
  disableMainAppointmentFetch = false,
}: ICalendarProps) {
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [showConfirmedAppointments, setShowConfirmedAppointments] =
    useState(false);

  const [
    selectedConfirmedAppoiontmentGroupId,
    setSelectedConfirmedAppoiontmentGroupId,
  ] = useState("");

  const [prospectiveTenants, setProspectiveTenants] = useState<
    IProspectiveTenant[]
  >([]);
  const navigate = useNavigate();

  const [availableAppointments, setAvailableAppointments] = useState<
    IAvailableAppointment[]
  >([]);
  const [relatedListings, setRelatedListings] = useState<IListing[]>([]);

  const showListingsWithScheduledAppointments = async () => {
    try {
      const listings = await fetchApartmentsListingsDataWithAppointments();

      setRelatedListings(listings);
    } catch (error) {
      console.error(
        "Error fetching listings with scheduled appointments:",
        error,
      );
    }
  };

  const showScheduledAppointments = async (listingID?: string) => {
    try {
      let appointments;

      if (listingID) {
        appointments = await fetchAppointmentsPerListingData(listingID);
      } else {
        appointments = await fetchConfirmedAppointments();
      }
      setAvailableAppointments(appointments);
    } catch (error) {
      console.error("Error fetching scheduled appointments:", error);
    }
  };

  useEffect(() => {
    if (showAvailableAppointments) {
      setCalendarEvents(
        availableAppointments.map((appointment) => ({
          id: appointment.id,
          title: showAvailableAppointments
            ? relatedListing[0].title
            : getListingNameById(appointment.listingID),
          start: appointment.startTime
            ? new Date(appointment.startTime)
            : new Date(),
          end: appointment.endTime ? new Date(appointment.endTime) : new Date(),
          capacity: appointment.capacity,
          listingID: appointment.listingID,
          tooltip: "doubleclick the appointment for more info",
          reminders: appointment.reminders,
          groupId: appointment.groupId,
        })),
      );
    } else {
      const uniqueGroupAppointments = Object.values(
        availableAppointments.reduce(
          (map, appointment) => {
            const groupId = appointment.groupId || appointment.id; // fallback to id if no groupId
            // Only store one per groupId — latest one wins (overwrite)
            map[groupId] = appointment;
            return map;
          },
          {} as { [key: string]: (typeof availableAppointments)[0] },
        ),
      );

      setCalendarEvents(
        uniqueGroupAppointments.map((appointment) => ({
          id: appointment.id,
          title: showAvailableAppointments
            ? relatedListing[0].title
            : getListingNameById(appointment.listingID),
          start: appointment.startTime
            ? new Date(appointment.startTime)
            : new Date(),
          end: appointment.endTime ? new Date(appointment.endTime) : new Date(),
          capacity: appointment.capacity,
          listingID: appointment.listingID,
          tooltip: "doubleclick the appointment for more info",
          reminders: appointment.reminders,
          groupId: appointment.groupId,
        })),
      );
    }
  }, [availableAppointments, relatedListings]);

  //for test:
  useEffect(() => {
    // This code will run whenever availableAppointments state changes

    if (calendarEvents.length > 0) {
      setListing(calendarEvents[0].listingID);
    }
  }, [calendarEvents]);

  useEffect(() => {
    if (relatedListings.length > 0) {
      setShowConfirmedAppointments(true);
    }
  }, [relatedListings]);

  function getListingNameById(specificListingID: string): string {
    const specificListing = relatedListings.find(
      (listing) => listing.id === specificListingID,
    );

    if (specificListing) {
      return specificListing.title;
    } else {
      // Listing with the specified ID not found
      return "undefined";
    }
  }

  const emptyAppointment: IAvailableAppointment = {
    id: "",
    listingID: "",
    startTime: new Date(),
    endTime: new Date(),
    capacity: 0,
    slotOrder: 0,
    reminders: [],
    groupId: "",
  };

  const [selectedAppointment, setSelectedAppointment] =
    useState<IAvailableAppointment>(emptyAppointment);

  const [showDefineSlotOrder, setShowDefineSlotOrder] = useState(false);
  const [showProspectiveTenant, setShowProspectiveTenant] = useState(false);
  const [listing, setListing] = useState("");
  const [openNotification, setOpenNotification] = useState(false);
  const [openAddAppointment, setOpenAddAppointment] = useState(false);

  const [showComposeEmail, setShowComposeEmail] = useState(false);
  const [selectedTenantsToEmail, setSelectedTenantsToEmail] = useState<
    Set<{ email: string; dept: string }>
  >(new Set());
  const [selectedAppartment, setSelectedAppartment] = useState<string>("");
  const handleEventDoubleClick = (event: CalendarEvent) => {
    if (relatedListings.length > 0) {
      setSelectedConfirmedAppoiontmentGroupId(event.groupId ?? "");
      setShowProspectiveTenant(true);
      setSelectedAppartment(event.title);
    } else {
      const updatedEvents = [...calendarEvents];

      // Find the event by its ID
      const eventIndex = updatedEvents.findIndex((e) => e.id === event.id);

      if (eventIndex !== -1) {
        const newSelectedAppointment: IAvailableAppointment = {
          id: updatedEvents[eventIndex].id,
          listingID: updatedEvents[eventIndex].listingID,
          startTime: dayjs(updatedEvents[eventIndex].start).toDate(), // Replace with the actual start time
          endTime: dayjs(updatedEvents[eventIndex].end).toDate(), // Replace with the actual end time
          capacity: updatedEvents[eventIndex].capacity,
          slotOrder: 0,
          reminders: updatedEvents[eventIndex].reminders,
          groupId: updatedEvents[eventIndex].groupId,
        };

        setSelectedAppointment(newSelectedAppointment);
        setOpenAddAppointment(true);
      }
    }
  };
  useEffect(() => {
    if (showAvailableAppointments) {
      showScheduledAppointments(relatedListing[0].id);
    } else {
      showListingsWithScheduledAppointments();
      showScheduledAppointments();
    }
  }, [openAddAppointment, showDefineSlotOrder, showProspectiveTenant]);
  const handleSlotsOrder = () => {
    setShowDefineSlotOrder(true);
  };

  return (
    <div className="w-full mt-14 ">
      <div className="flex items-center justify-between px-4 py-2 mb-4 ">
        <div className="flex items-center space-x-3">
          <div
            title="Home"
            className="w-8 h-8 flex items-center text-white justify-center border rounded cursor-pointer bg-indigo-600 hover:bg-indigo-700"
          >
            <KeyboardBackspaceIcon
              fontSize="small"
              onClick={() =>
                setShowCalendar ? setShowCalendar(false) : navigate("/listing")
              }
            />
          </div>

          <h2 className="text-lg font-semibold text-gray-900">
            {showConfirmedAppointments
              ? "Scheduled Appointments"
              : `Available Appointments for ${relatedListing[0].title}`}
          </h2>
        </div>

        {/* {showAvailableAppointments && (
          <button
            type="button"
            className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus:outline-none"
            onClick={handleSlotsOrder}
          >
            Slots Order
          </button>
        )} */}
      </div>

      <Notification
        isOpen={openNotification}
        messageType="success"
        message="The appointment is cancelled successfully"
        setIsOpen={setOpenNotification}
      />

      <div className="bg-white rounded-xl shadow-md p-4 border">
        <BigCalendar
          localizer={localizer}
          events={calendarEvents}
          startAccessor="start"
          endAccessor="end"
          tooltipAccessor="tooltip"
          style={{
            height: "90vh",
          }}
          onDoubleClickEvent={handleEventDoubleClick}
          eventPropGetter={(event, start, end, isSelected) => ({
            className: "custom-event-class",
            style: {
              backgroundColor: "#4f46e5", // modern indigo
              color: "white",
              borderRadius: "6px",
              padding: "2px 6px",
              fontSize: "0.8rem",
              height: "auto",
              border: "none",
              width: "auto",
              margin: "1px 6px",
            },
          })}
        />
      </div>

      <div
        id="tooltip-default"
        role="tooltip"
        className="absolute z-10 invisible inline-block px-3 py-2 text-sm font-medium text-white transition-opacity duration-300 bg-gray-900 rounded-lg shadow-sm opacity-0 tooltip dark:bg-gray-700"
      >
        Double-click for more info
        <div className="tooltip-arrow" data-popper-arrow></div>
      </div>
      <AddAppointment
        open={openAddAppointment}
        setOpen={setOpenAddAppointment}
        isVacant={
          relatedListings.length === 0
            ? relatedListing[0]?.isVacant
            : relatedListings[0]?.isVacant
        }
        listingId={selectedAppointment?.listingID ?? ""}
        appointmentToBeEdited={selectedAppointment}
        loadMainAppointment={!disableMainAppointmentFetch}
      />
      <DefineSlotOrder
        open={showDefineSlotOrder}
        setOpen={setShowDefineSlotOrder}
        availableAppointments={availableAppointments}
      />
      <ProspectiveTenants
        openNotification={openNotification}
        setOpenNotification={setOpenNotification}
        open={showProspectiveTenant}
        setOpen={setShowProspectiveTenant}
        setShowComposeEmail={setShowComposeEmail}
        setSelectedTenantsToEmail={setSelectedTenantsToEmail}
        Appartment={selectedAppartment}
        groupId={selectedConfirmedAppoiontmentGroupId}
      />
      <ComposeEmail
        emails={selectedTenantsToEmail}
        open={showComposeEmail}
        setOpen={setShowComposeEmail}
        selectedEmails={selectedTenantsToEmail}
      />
    </div>
  );
}

export default Calendar;
